import React, { useState, useEffect } from "react";
import { 
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, 
  Snackbar, Alert, Divider
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

export default function ComplianceChecksManager({ accountId, accountName }) {
  const [assessmentChecks, setAssessmentChecks] = useState([]);
  const [bcaChecks, setBcaChecks] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCheck, setEditingCheck] = useState(null);
  const [currentType, setCurrentType] = useState(''); // 'assessment' or 'bca'
  const [formData, setFormData] = useState({ rule_regulation: "", compliance_item: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    if (accountId) {
      fetchComplianceChecks();
    }
  }, [accountId]);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchComplianceChecks = async () => {
    try {
      // Fetch Assessment Compliance Checks
      const assessmentResponse = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/accounts/${accountId}/assessment-compliance-checks`,
        { credentials: "include" }
      );
      if (assessmentResponse.ok) {
        const assessmentData = await assessmentResponse.json();
        setAssessmentChecks(assessmentData);
      }

      // Fetch BCA Compliance Checks
      const bcaResponse = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/accounts/${accountId}/bca-compliance-checks`,
        { credentials: "include" }
      );
      if (bcaResponse.ok) {
        const bcaData = await bcaResponse.json();
        setBcaChecks(bcaData);
      }
    } catch (error) {
      showSnackbar("Error fetching compliance checks", "error");
    }
  };

  const handleAddCheck = (type) => {
    setCurrentType(type);
    setEditingCheck(null);
    setFormData({ rule_regulation: "", compliance_item: "" });
    setOpenDialog(true);
  };

  const handleEditCheck = (check, type) => {
    setCurrentType(type);
    setEditingCheck(check);
    setFormData({ 
      rule_regulation: check.rule_regulation, 
      compliance_item: check.compliance_item 
    });
    setOpenDialog(true);
  };

  const handleSaveCheck = async () => {
    try {
      const url = editingCheck
        ? `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/accounts/${accountId}/${currentType}-compliance-checks/${editingCheck.id}`
        : `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/accounts/${accountId}/${currentType}-compliance-checks`;
      
      const method = editingCheck ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setOpenDialog(false);
        fetchComplianceChecks();
        showSnackbar(
          editingCheck ? "Compliance check updated successfully" : "Compliance check created successfully",
          "success"
        );
      } else {
        showSnackbar("Error saving compliance check", "error");
      }
    } catch (error) {
      showSnackbar("Error saving compliance check", "error");
    }
  };

  const handleDeleteCheck = async (checkId, type) => {
    if (!window.confirm("Are you sure you want to delete this compliance check?")) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/accounts/${accountId}/${type}-compliance-checks/${checkId}`,
        { method: "DELETE", credentials: "include" }
      );

      if (response.ok) {
        fetchComplianceChecks();
        showSnackbar("Compliance check deleted successfully", "success");
      } else {
        showSnackbar("Error deleting compliance check", "error");
      }
    } catch (error) {
      showSnackbar("Error deleting compliance check", "error");
    }
  };

  const renderChecksTable = (checks, type, title) => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddCheck(type)}
          size="small"
        >
          Add Check
        </Button>
      </Box>
      
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Rule/Regulation</strong></TableCell>
            <TableCell><strong>Compliance Item</strong></TableCell>
            <TableCell><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {checks.map((check) => (
            <TableRow key={check.id}>
              <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {check.rule_regulation}
              </TableCell>
              <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {check.compliance_item}
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={() => handleEditCheck(check, type)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteCheck(check.id, type)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {checks.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                No {title.toLowerCase()} configured
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Compliance Checks - {accountName}
      </Typography>
      
      {renderChecksTable(assessmentChecks, 'assessment', 'Additional Assessment Compliance Checks')}
      <Divider sx={{ my: 3 }} />
      {renderChecksTable(bcaChecks, 'bca', 'Additional BCA Compliance Checks')}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCheck ? 'Edit' : 'Add'} {currentType === 'assessment' ? 'Assessment' : 'BCA'} Compliance Check
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Rule/Regulation"
            fullWidth
            value={formData.rule_regulation}
            onChange={(e) => setFormData({ ...formData, rule_regulation: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g., ABCB Housing Provision 3.2"
          />
          <TextField
            label="Compliance Item"
            fullWidth
            multiline
            rows={4}
            value={formData.compliance_item}
            onChange={(e) => setFormData({ ...formData, compliance_item: e.target.value })}
            placeholder="e.g., Retaining walls: designed to AS 4678"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveCheck} 
            variant="contained"
            disabled={!formData.rule_regulation.trim() || !formData.compliance_item.trim()}
          >
            {editingCheck ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}